import { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../config/firebase';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  createUserWithEmailAndPassword,
  sendEmailVerification as sendFirebaseEmailVerification,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { api } from '../services/api';
import { setupAuthRefresh } from '../config/auth';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [backendError, setBackendError] = useState(null);

  async function register(email, password) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();
      api.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
      
      // Send verification email
      await sendFirebaseEmailVerification(userCredential.user);
      setVerificationEmailSent(true);
      setNeedsVerification(true);
      
      // Update user state
      setUser({
        firebaseUser: userCredential.user,
        token: idToken
      });
    } catch (error) {
      throw error;
    }
  }

  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();
      api.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
      
      if (!userCredential.user.emailVerified) {
        setNeedsVerification(true);
        throw new Error('Please verify your email before logging in');
      }
      
      setUser({
        firebaseUser: userCredential.user,
        token: idToken
      });
    } catch (error) {
      throw error;
    }
  }

  async function logout() {
    await signOut(auth);
    setUser(null);
    api.defaults.headers.common['Authorization'] = '';
  }

  async function sendEmailVerification() {
    if (user?.firebaseUser && !user.firebaseUser.emailVerified) {
      await sendFirebaseEmailVerification(user.firebaseUser);
      setVerificationEmailSent(true);
    }
  }

  async function reloadUser() {
    if (user?.firebaseUser) {
      await user.firebaseUser.reload();
      const idToken = await user.firebaseUser.getIdToken(true);
      setUser({
        firebaseUser: auth.currentUser,
        token: idToken
      });
      setNeedsVerification(!auth.currentUser.emailVerified);
    }
  }

  async function loginWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const idToken = await userCredential.user.getIdToken();
      api.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
      
      setUser({
        firebaseUser: userCredential.user,
        token: idToken
      });
    } catch (error) {
      throw error;
    }
  }

  // Function to refresh the token
  async function refreshToken() {
    if (!user?.firebaseUser || isRefreshing) return null;
    
    try {
      setIsRefreshing(true);
      const idToken = await user.firebaseUser.getIdToken(true);
      api.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
      
      setUser(prev => ({
        ...prev,
        token: idToken
      }));
      
      return idToken;
    } catch (error) {
      console.error('Error refreshing token:', error);
      // If we can't refresh the token, log the user out
      await logout();
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }

  useEffect(() => {
    // Set up the auth refresh handler
    setupAuthRefresh(() => refreshToken());

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const idToken = await firebaseUser.getIdToken();
          setUser({
            firebaseUser,
            token: idToken
          });
          setNeedsVerification(!firebaseUser.emailVerified);
          api.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
          setBackendError(null);
        } catch (error) {
          console.error('Backend connection error:', error);
          setBackendError('Unable to connect to the server. Please try again later.');
        }
      } else {
        setUser(null);
        api.defaults.headers.common['Authorization'] = '';
        setBackendError(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    user,
    login,
    logout,
    register,
    loading,
    sendEmailVerification,
    reloadUser,
    needsVerification,
    verificationEmailSent,
    updateUser: setUser,
    loginWithGoogle,
    refreshToken,
    isRefreshing,
    backendError
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 